import type { Snapshot, Lane, RoutingGate } from "../types";
import { laneBrand } from "../lanes";

// MetricsTab — Prometheus is not wired in the fleet today. Instead of pinning
// every panel on a red "Prometheus unreachable" badge with zeros, derive
// lightweight metrics directly from the aggregator snapshot:
//   - active desk count + by brand
//   - last turn time per desk (from lane.last_action_ts)
//   - frankie todos open / urgent
//   - veyra forecast + stuck gates
//   - peak revenue + customer queue
// When the aggregator itself is offline we fall back to a single calm panel
// rather than a wall of zeros.

export function MetricsTab({ snap }: { snap: Snapshot | null }) {
  if (!snap) {
    return (
      <div className="space-y-5">
        <div className="bg-panel border border-edge rounded-md p-4">
          <div className="text-mist text-xs uppercase tracking-wide mb-2">Metrics</div>
          <div className="text-sm">Aggregator offline — soft-empty view.</div>
          <div className="text-mist text-xs mt-1">
            Metrics derive from live fleet state; will fill in when the feed reconnects.
          </div>
        </div>
        <div className="bg-panel border border-edge rounded-md p-3 text-xs text-mist">
          Note · Prometheus is not wired in the fleet. These metrics come straight off the aggregator, not a TSDB.
        </div>
      </div>
    );
  }

  const lanes = snap.fleet.lanes;
  const now = Date.now();
  const isActive = (l: Lane) => {
    if (!l.last_action_ts) return false;
    const t = Date.parse(l.last_action_ts);
    if (Number.isNaN(t)) return false;
    return now - t < 60 * 60 * 1000; // active within last hour
  };
  const active = lanes.filter(isActive);
  const activeByBrand = {
    veyrapay: active.filter((l) => laneBrand(l.id) === "veyrapay").length,
    peak: active.filter((l) => laneBrand(l.id) === "peak").length,
    skinwire: active.filter((l) => laneBrand(l.id) === "skinwire").length,
    labs: active.filter((l) => laneBrand(l.id) === "labs").length,
    cross: active.filter((l) => laneBrand(l.id) === "cross").length,
  };
  const urgentTodos = snap.frankie_todos.filter((t) => t.urgent).length;

  const tiles = [
    { label: "Active desks (1h)", value: active.length, sub: `of ${lanes.length} total` },
    { label: "VeyraPay active", value: activeByBrand.veyrapay, sub: "ash · ralph · vex · forge" },
    { label: "Peak active", value: activeByBrand.peak, sub: "dingo · milo" },
    { label: "Cross active", value: activeByBrand.cross, sub: "tess · frankie · doc · …" },
    { label: "Frankie todos", value: snap.frankie_todos.length, sub: `${urgentTodos} urgent` },
    { label: "Inbox", value: snap.inbox.length, sub: "pending human triage" },
    {
      label: "Veyra throughput",
      value: `$${Math.round(snap.veyra.forecast_today_usd).toLocaleString()}`,
      sub: `${snap.veyra.shield.live_count} live · ${snap.veyra.stuck_gates.length} stuck`,
    },
    {
      label: "Peak revenue",
      value: `$${Math.round(snap.peak.revenue_today_usd).toLocaleString()}`,
      sub: `${snap.peak.orders_today_count} orders · queue ${snap.peak.customer_queue_count}`,
    },
  ];

  // Last turn per desk — sorted by most recent activity, then idle desks last.
  const turnRows = [...lanes].sort((a, b) => {
    const ta = a.last_action_ts ? Date.parse(a.last_action_ts) : 0;
    const tb = b.last_action_ts ? Date.parse(b.last_action_ts) : 0;
    return tb - ta;
  });

  return (
    <div className="space-y-5">
      <div className="bg-panel border border-edge rounded-md p-3 text-xs text-mist">
        Metrics derived from aggregator state. Prometheus is not wired — no TSDB, no cost panel.
      </div>
      <RoutingPoolPanel rp={snap.routing_pool} />
      <BurnLeaderboardPanel burn={snap.burn_leaderboard} />
      <div className="grid grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="bg-panel border border-edge rounded-md p-3">
            <div className="text-mist text-xs uppercase tracking-wide">{t.label}</div>
            <div className="text-2xl mt-1">{t.value}</div>
            <div className="text-mist text-xs mt-0.5">{t.sub}</div>
          </div>
        ))}
      </div>
      <div className="bg-panel border border-edge rounded-md">
        <div className="px-3 py-2 border-b border-edge text-xs uppercase tracking-wide">Last turn per desk</div>
        <div className="divide-y divide-edge">
          {turnRows.map((l) => (
            <div key={l.id} className="px-3 py-1.5 flex items-center text-xs">
              <span className="w-6">{l.emoji}</span>
              <span className="w-24">{l.name}</span>
              <span className="w-24 text-mist">{laneBrand(l.id)}</span>
              <span className="w-20 text-mist">{l.status}</span>
              <span className="w-20 text-mist">{relTs(l.last_action_ts)}</span>
              <span className="flex-1 truncate text-mist">{l.last_action_summary || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BurnLeaderboardPanel({ burn }: { burn: Snapshot["burn_leaderboard"] }) {
  if (!burn || burn.desks.length === 0) {
    return (
      <div className="bg-panel border border-edge rounded-md p-4">
        <div className="text-mist text-xs uppercase tracking-wide mb-1">Desk Burn · 24h</div>
        <div className="text-sm text-mist">Burn leaderboard not yet reported — generated on MBP, pushed every 20 min.</div>
      </div>
    );
  }
  const desks = [...burn.desks].sort((a, b) => b.est_usd - a.est_usd);
  const max = Math.max(1, desks[0]?.est_usd ?? 1);
  const total = desks.reduce((s, d) => s + d.est_usd, 0);
  // Heat ramp by rank — top burners run hotter.
  const fillClass = (i: number) =>
    i === 0 ? "bg-red-500" : i <= 2 ? "bg-orange-500" : i <= 5 ? "bg-amber-500" : "bg-emerald-500";
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <div className="bg-panel border border-edge rounded-md">
      <div className="px-3 py-2 border-b border-edge flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide">Desk Burn · 24h token spend</span>
        <span className="flex items-center gap-2">
          <span className="text-sm tabular-nums">${Math.round(total).toLocaleString()} total</span>
          {burn.stale ? (
            <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-300">
              stale{burn.age_min != null ? ` ${burn.age_min}m` : ""}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-300">
              live{burn.age_min != null ? ` ${burn.age_min}m` : ""}
            </span>
          )}
        </span>
      </div>
      <div className="divide-y divide-edge">
        {desks.map((d, i) => {
          const pct = Math.min(100, (d.est_usd / max) * 100);
          return (
            <div key={d.desk} className="px-3 py-1.5 flex items-center gap-3 text-xs">
              <span className="w-6 shrink-0 text-mist tabular-nums">{i + 1}</span>
              <span className="w-28 shrink-0 truncate" title={d.desk}>{cap(d.desk)}</span>
              <div className="flex-1 relative bg-edge rounded-full h-1.5 overflow-hidden">
                <div className={`${fillClass(i)} h-full rounded-full`} style={{ width: `${pct}%` }} />
              </div>
              <span className="w-36 shrink-0 text-right text-mist tabular-nums">
                ${d.est_usd.toFixed(0)} · {d.tokens.toFixed(0)}M tok
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoutingPoolPanel({ rp }: { rp: Snapshot["routing_pool"] }) {
  if (!rp || (rp.in_pool.length === 0 && rp.out_pool.length === 0)) {
    return (
      <div className="bg-panel border border-edge rounded-md p-4">
        <div className="text-mist text-xs uppercase tracking-wide mb-1">Routing Pool</div>
        <div className="text-sm text-mist">Routing pool snapshot stale or empty — brain unreachable.</div>
      </div>
    );
  }
  const fillClass = (c: string) => (c === "red" ? "bg-red-500" : c === "amber" ? "bg-amber-500" : "bg-emerald-500");
  const Bar = ({ g, dim }: { g: RoutingGate; dim?: boolean }) => {
    const fillPct = g.hard_cap_24h > 0 ? Math.min(100, (g.used_24h / g.hard_cap_24h) * 100) : 0;
    const safePct = g.hard_cap_24h > 0 ? Math.min(100, (g.safe_cap_24h / g.hard_cap_24h) * 100) : 70;
    return (
      <div className={`px-3 py-1.5 flex items-center gap-3 text-xs ${dim ? "opacity-50" : ""}`}>
        <span className="w-40 shrink-0 truncate text-mist" title={g.label}>{g.label.slice(0, 24)}</span>
        <div className="flex-1 relative bg-edge rounded-full h-1.5 overflow-hidden">
          <div className={`${fillClass(g.color)} h-full rounded-full`} style={{ width: `${fillPct}%` }} />
          <div className="absolute top-0 bottom-0 w-px bg-mist/60" style={{ left: `${safePct}%` }} />
        </div>
        <span className="w-48 shrink-0 text-right text-mist tabular-nums">
          A${g.used_24h.toLocaleString()} / {g.hard_cap_24h.toLocaleString()} · {Math.round(g.headroom_pct * 100)}% free
          {dim ? <span className="ml-1 text-red-400">{g.killed ? "killed" : g.identity_status}</span> : null}
        </span>
      </div>
    );
  };
  const Chip = ({ n, cls, label }: { n: number; cls: string; label: string }) => (
    <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{n} {label}</span>
  );
  return (
    <div className="bg-panel border border-edge rounded-md">
      <div className="px-3 py-2 border-b border-edge flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide">Routing Pool · 24h capacity</span>
        <span className="flex items-center gap-2">
          <span className="text-sm">A${rp.aggregate.total_headroom_aud.toLocaleString()} headroom</span>
          <Chip n={rp.aggregate.n_green} cls="bg-emerald-500/20 text-emerald-300" label="green" />
          <Chip n={rp.aggregate.n_amber} cls="bg-amber-500/20 text-amber-300" label="amber" />
          <Chip n={rp.aggregate.n_red} cls="bg-red-500/20 text-red-300" label="red" />
        </span>
      </div>
      <div className="divide-y divide-edge">
        {rp.in_pool.map((g) => <Bar key={g.slug} g={g} />)}
        {rp.out_pool.map((g) => <Bar key={g.slug} g={g} dim />)}
      </div>
    </div>
  );
}

function relTs(ts: string | null | undefined) {
  if (!ts) return "—";
  try {
    const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch {
    return "—";
  }
}
